import React, { useState, useEffect } from 'react';
import { useGetUsersQuery, useGetCurrentUserQuery, useDeleteUserMutation } from '../features/users/api/usersApiSlice';
import { User } from '../features/users/types';
import { useGetEmpresasQuery } from '../features/empresas/api/empresasApiSlice';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAdmin, selectIsSuperAdmin } from '../features/auth/authSlice';
import { UserForm } from '../features/users/components/UserForm';
import { showToast } from '../components/ui/Toast';

// Función para obtener etiqueta de rol según role
const getRoleLabel = (role: string): string => {
  switch (role) {
    case 'superadmin':
      return 'Superadmin';
    case 'admin':
      return 'Administrador';
    case 'user':
      return 'Usuario';
    default:
      return 'Desconocido';
  }
};

// Función para obtener color de badge según role
const getRoleBadgeClasses = (role: string): string => {
  switch (role) {
    case 'superadmin':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    case 'admin':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    case 'user':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const UsuariosPage = () => {
  // Estados para manejar modales y edición
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Obtener usuario actual y verificar si es admin o superadmin
  const currentAuthUser = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectIsAdmin);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);

  // Estado para mostrar a los usuarios
  const [usuarios, setUsuarios] = useState<User[]>([]);
  
  // Query y Mutations de RTK Query
  const { data: userData, isLoading: isUserLoading, isError: isUserError, error: userError } = 
    useGetCurrentUserQuery();
  
  // Si somos superadmin, obtenemos TODOS los usuarios directamente
  const { data: allUsersData, isLoading: isAllUsersLoading } = 
    useGetUsersQuery(
      { page: 1, limit: 100 }, // Parámetros básicos para obtener todos
      { skip: !isSuperAdmin } // Solo ejecutar si es superadmin
    );
  
  // Obtener usuarios de la empresa cuando tenemos un usuario autenticado y es admin (no superadmin) o usuario normal
  const { data: empresaData, isLoading: isEmpresaLoading } = 
    useGetUsersQuery(
      currentAuthUser?.empresa_id && !isSuperAdmin
        ? { empresaId: currentAuthUser.empresa_id }
        : {}, // Objeto vacío para cumplir con la firma del hook
      { skip: !currentAuthUser?.empresa_id || isSuperAdmin }
    );
  
  // Si somos admin o superadmin, también obtenemos todas las empresas
  const { data: empresasData, isLoading: isEmpresasLoading } = 
    useGetEmpresasQuery(undefined, { skip: !(isAdmin || isSuperAdmin) });
  
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  // Efecto para combinar los usuarios
  useEffect(() => {
    console.log('Actualizando usuarios, isSuperAdmin:', isSuperAdmin);
    console.log('allUsersData:', allUsersData);
    console.log('empresaData:', empresaData);
    
    let usersToSet: User[] = [];
    
    // Si es superadmin y tenemos todos los usuarios
    if (isSuperAdmin && allUsersData?.users) {
      usersToSet = [...allUsersData.users];
    }
    // Si es admin normal o usuario común, solo mostramos usuarios de su empresa
    else if (currentAuthUser?.empresa_id && empresaData?.users) {
      usersToSet = [...empresaData.users];
    }
    // Caso de usuario individual sin empresa o datos insuficientes
    else if (userData) {
      usersToSet.push(userData as User);
    }
    
    // Ordenar usuarios alfabéticamente por email antes de establecerlos en el estado
    usersToSet.sort((a, b) => a.email.localeCompare(b.email));
    
    // Actualizar el estado con los usuarios ordenados
    setUsuarios(usersToSet);
  }, [userData, empresaData, allUsersData, isSuperAdmin, currentAuthUser?.empresa_id]);

  // Handlers para acciones de la tabla
  const handleCreate = () => {
    setModalMode('create');
    setCurrentUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    // Verificar si el admin tiene permiso para editar este usuario (debe ser de su empresa)
    if (isAdmin && !isSuperAdmin && user.empresa_id !== currentAuthUser?.empresa_id) {
      showToast('No tienes permiso para editar usuarios de otras empresas', 'warning');
      return;
    }
    // Superadmin puede editar cualquier usuario
    setModalMode('edit');
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    // Verificar si es admin o superadmin
    if (!(isAdmin || isSuperAdmin)) {
      showToast('Solo los administradores pueden eliminar usuarios', 'warning');
      return;
    }
    // Buscar el usuario a eliminar
    const userToDelete = usuarios.find(user => user.id === id);
    // Verificar si el admin tiene permiso para eliminar este usuario (debe ser de su empresa)
    if (isAdmin && !isSuperAdmin && userToDelete && userToDelete.empresa_id !== currentAuthUser?.empresa_id) {
      showToast('No tienes permiso para eliminar usuarios de otras empresas', 'warning');
      return;
    }
    if (confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await deleteUser(id).unwrap();
        showToast('Usuario eliminado con éxito', 'success');
        // Actualizar la lista de usuarios local
        setUsuarios(usuarios.filter(user => user.id !== id));
      } catch (err) {
        console.error('Error al eliminar usuario:', err);
        showToast('Error al eliminar usuario', 'error');
      }
    }
  };

  // Verificar estado de carga
  const isLoading = isUserLoading || isEmpresaLoading || 
                   (isAdmin && isEmpresasLoading) || 
                   (isSuperAdmin && isAllUsersLoading);

  // Renderizado condicional basado en el estado de la carga
  if (isLoading) {
    return <div className="text-center py-10">Cargando usuarios...</div>;
  }

  if (isUserError) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md mb-4">
        <p>Error al cargar usuario: {typeof userError === 'object' && userError && 'data' in userError ? (userError as { data?: { message?: string } })?.data?.message : 'Ocurrió un error desconocido'}</p>
      </div>
    );
  }

  // Función auxiliar para obtener el nombre de la empresa por ID
  const getEmpresaNombre = (empresaId: number | null | undefined) => {
    if (!empresaId || !empresasData?.empresas) return '-';
    const empresa = empresasData.empresas.find(e => e.id === empresaId);
    return empresa ? empresa.nombre : '-';
  };
  
  // Si no hay datos
  if (usuarios.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">Gestión de Usuarios</h1>
          {(isAdmin || isSuperAdmin) && (
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nuevo Usuario
            </button>
          )}
        </div>
        
        <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border text-center py-8">
          <p className="text-muted-foreground">No hay usuarios para mostrar.</p>
        </div>

        {/* Modal para crear usuario */}
        {isModalOpen && (isAdmin || isSuperAdmin) && empresasData?.empresas && (
          <UserForm
            mode={modalMode}
            user={currentUser}
            empresas={empresasData.empresas}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Gestión de Usuarios</h1>
        {(isAdmin || isSuperAdmin) && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuevo Usuario
          </button>
        )}
      </div>

      <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border">
        <p className="text-muted-foreground mb-4">
          {(isAdmin || isSuperAdmin)
            ? "Administra los usuarios del sistema y asígnales empresas."
            : "Visualiza los usuarios de tu empresa."}
        </p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse table-auto">
            <thead>
              <tr className="bg-muted">
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Rol</th>
                {(isAdmin || isSuperAdmin) && <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Empresa</th>}
                {(isAdmin || isSuperAdmin) && <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Acciones</th>}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {usuarios.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{user.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClasses(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  {(isAdmin || isSuperAdmin) && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {getEmpresaNombre(user.empresa_id)}
                    </td>
                  )}
                  {(isAdmin || isSuperAdmin) && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button 
                        onClick={() => handleEdit(user)} 
                        className="text-primary hover:text-primary/80 mr-4 inline-flex items-center"
                        disabled={isDeleting}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Editar
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id)} 
                        className="text-destructive hover:text-destructive/80 inline-flex items-center"
                        disabled={isDeleting}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Eliminar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para crear/editar usuario */}
      {isModalOpen && (isAdmin || isSuperAdmin) && (
        <UserForm
          mode={modalMode}
          user={currentUser}
          empresas={empresasData?.empresas || []}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}; 